import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHaptics } from '../../utils/haptics'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import { useMoney } from '../../hooks/usePublicSettings'
import { jobsApi, statusLabel } from '../../utils/accountApi'

// Icons
function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function PhoneIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  )
}

function MessageCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

function TruckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16,8 20,8 23,11 23,16 16,16"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  )
}

const OPEN_STAGES = ['requested', 'scheduled', 'active']

export default function OrderTracking() {
  const navigate = useNavigate()
  const haptics = useHaptics()
  const money = useMoney()
  const [activeTab, setActiveTab] = useState('active')
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      setOrders(await jobsApi.orders())
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const activeOrders = orders.filter(o => OPEN_STAGES.includes(o.stage))
  const orderHistory = orders.filter(o => !OPEN_STAGES.includes(o.stage))

  const getStatusColor = (stage) => {
    switch (stage) {
      case 'active': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'requested': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-slate-100 text-slate-800'
    }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
  const formatDateTime = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  const open = (o) => {
    haptics.light()
    if (o.job_id) navigate(`/jobs/${o.job_id}`)
  }

  const renderStars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={`text-sm ${i < rating ? 'text-yellow-400' : 'text-slate-300'}`}>★</span>
  ))

  const status = o => statusLabel(o.job_status || o.request_status)

  return (
    <PageLayout
      title="Track Orders"
      subtitle="Monitor your service requests and history"
      onRefresh={load}
      showBackButton={true}
    >
      {/* Tab Navigation */}
      <div className="flex bg-white rounded-2xl p-1 shadow-sm">
        {[['active', `Active Orders (${activeOrders.length})`], ['history', `Order History (${orderHistory.length})`]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-colors touch-manipulation ${
              activeTab === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <Card><div className="text-sm text-slate-500">Loading…</div></Card>}
      {error && <Card><div className="text-sm text-red-600">Could not load your orders: {error}</div></Card>}

      {/* Active Orders Tab */}
      {!loading && activeTab === 'active' && (
        <div className="space-y-6">
          {activeOrders.length > 0 ? (
            activeOrders.map(order => (
              <div key={order.request_id} className="space-y-4">
                <Card hover={!!order.job_id} onClick={order.job_id ? () => open(order) : undefined}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{order.title}</h3>
                      {order.description && <p className="text-sm text-slate-600">{order.description}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        {order.job_number ? `Job #${order.job_number}` : `Requested ${formatDate(order.requested_at)}`}
                        {order.scheduled_start && ` · Visit ${formatDateTime(order.scheduled_start)}`}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.stage)}`}>
                      {status(order)}
                    </span>
                  </div>
                </Card>

                {/* Progress Tracking: the recorded status changes */}
                <Card>
                  <h4 className="font-semibold text-slate-900 mb-4">Progress Tracking</h4>
                  <div className="space-y-3">
                    {order.timeline.map((e, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
                          <CheckCircleIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{statusLabel(e.status)}</div>
                          <div className="text-xs text-slate-500">{formatDateTime(e.at)}{e.note ? ` · ${e.note}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Contractor Info & Actions */}
                {order.professional && (
                  <Card>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-slate-900">{order.professional.name}</h4>
                        {order.professional.phone && <p className="text-sm text-slate-600">{order.professional.phone}</p>}
                      </div>
                      <TruckIcon className="text-slate-400" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => open(order)}
                        className="flex items-center justify-center gap-2 py-3 px-4 border border-slate-300 rounded-xl text-sm font-medium hover:bg-slate-50 touch-manipulation"
                      >
                        <MessageCircleIcon />
                        Details
                      </button>
                      <a
                        href={order.professional.phone ? `tel:${order.professional.phone}` : undefined}
                        title={order.professional.phone ? undefined : 'No phone number on file'}
                        className={`flex items-center justify-center gap-2 py-3 px-4 border border-slate-300 rounded-xl text-sm font-medium touch-manipulation ${
                          order.professional.phone ? 'hover:bg-slate-50' : 'opacity-50 pointer-events-none'
                        }`}
                      >
                        <PhoneIcon />
                        Call
                      </a>
                    </div>
                  </Card>
                )}
              </div>
            ))
          ) : (
            <Card className="text-center py-12">
              <TruckIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">No Active Orders</h3>
              <p className="text-slate-600 text-sm">You don't have any active service orders at the moment.</p>
            </Card>
          )}
        </div>
      )}

      {/* Order History Tab */}
      {!loading && activeTab === 'history' && (
        <div className="space-y-4">
          {orderHistory.length === 0 && (
            <Card><div className="text-center py-8 text-sm text-slate-600">No past orders yet.</div></Card>
          )}
          {orderHistory.map(order => {
            const ended = order.timeline[order.timeline.length - 1]
            return (
              <Card key={order.request_id} hover={!!order.job_id} onClick={order.job_id ? () => open(order) : undefined}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{order.title}</h3>
                    {order.description && <p className="text-sm text-slate-600">{order.description}</p>}
                    {order.job_number && <p className="text-xs text-slate-500 mt-1">Job #{order.job_number}</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.stage)}`}>
                    {status(order)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-3">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <ClockIcon />
                      <span className="font-medium">Order Date</span>
                    </div>
                    <span>{formatDate(order.requested_at)}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <MapPinIcon className="w-4 h-4" />
                      <span className="font-medium">Location</span>
                    </div>
                    <span>{order.place || '—'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{order.professional?.name || '—'}</span>
                    {order.customer_rating && (
                      <div className="flex items-center gap-1">{renderStars(order.customer_rating)}</div>
                    )}
                  </div>
                  {order.total_amount != null && order.stage === 'completed' && (
                    <span className="text-sm font-semibold text-slate-900">{money(order.total_amount)}</span>
                  )}
                </div>

                {order.stage === 'cancelled' && ended && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-red-600">Cancelled {formatDate(ended.at)}{ended.note ? `: ${ended.note}` : ''}</p>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </PageLayout>
  )
}
